/**
 * Event Subscription Observable Item Object Definition
 * @typedef {Object} EventSubscriptionObservableItem
 * @property {boolean} active - Indicates if the subscription is active
 * @property {string} field - The aws appSync query field to subscribe to
 * @property {Object|null} subscription - The subscription state if active
 * @property {string[]} modules - The modules to activate the subscription
 * @property {Object} action - The action to perform when the event is triggered
 * @property {string} action.type - The type of action to dispatch
 * @property {string} action.property - The context capture property for the results of the action
 * @property {boolean} [action.recalculate=false] - Whether to recalculate event data after the action
 * @property {boolean} [action.recalendar=false] - Whether to refresh the event calendar after the action
 * @property {boolean} [action.reticket=false] - Whether to refresh the event tickets after the action
 */

/** * Create Event Subscription Observable Item Object
 * @param {Object} params
 * @param {boolean} [params.active=false] - Indicates if the subscription is active
 * @param {string} params.field - The aws appSync query field to subscribe to
 * @param {string[]} params.modules - The modules to activate the subscription
 * @param {Object} params.action - The action to perform when the event is triggered
 * @param {string} params.action.type - The type of action to dispatch
 * @param {string} params.action.property - The context capture property for the results of the action
 * @param {boolean} [params.action.recalculate=false] - Whether to recalculate event data after the action
 * @param {boolean} [params.action.recalendar=false] - Whether to refresh the event calendar after the action
 * @param {boolean} [params.action.reticket=false] - Whether to refresh the event tickets after the action
 * @returns {EventSubscriptionObservableItem} The created Event Subscription Observable Item Object
 */
export function createEventSubscriptionObservableItem({
  active = false,
  field,
  modules = [],
  action = {},
  params = {},
}) {
  return {
    active,
    field,
    subscription: null,
    modules,
    action: {
      type: action.type,
      property: action.property,
      recalculate: action.recalculate || false,
      recalendar: action.recalendar || false,
      reticket: action.reticket || false,
    },
    params,
  };
}

/**
 * Event Subscription Observable List Object Definition
 * @typedef {Object} EventSubscriptionObservableList
 * @property {EventSubscriptionObservableItem} event - The subscription observable item for event updates
 * @property {EventSubscriptionObservableItem} sharing - The subscription observable item for sharing updates
 * @property {EventSubscriptionObservableItem} locations - The subscription observable item for location updates
 * @property {EventSubscriptionObservableItem} messaging - The subscription observable item for messaging updates
 * @property {EventSubscriptionObservableItem} entry - The subscription observable item for entry handshakes
 */

/**
 * Create Event Subscription Observable List Object
 * @returns {EventSubscriptionObservableList} The created Event Subscription Observable List Object
 */
export function createEventSubscriptionObservableList() {
  return {
    event: createEventSubscriptionObservableItem({
      active: false,
      field: "onDeltaEvent",
      modules: [
        "launchpad",
        "dates",
        "tickets",
        "gallery",
        "members",
        "store",
        "messaging",
        "entry",
        "locations",
        "sharing",
      ],
      action: {
        type: "RELOAD_EVENT",
        property: "data",
        recalculate: true,
        recalendar: true,
        reticket: true,
      },
    }),
    sharing: createEventSubscriptionObservableItem({
      active: false,
      field: "onDeltaSharing",
      modules: ["sharing"],
      action: {
        type: "PULL_LINKS",
        property: "content",
        recalculate: true,
      },
    }),
    locations: createEventSubscriptionObservableItem({
      active: false,
      field: "onDeltaLocations",
      modules: ["launchpad", "locations"],
      action: {
        type: "PULL_PLACES",
        property: "content",
        recalculate: true,
      },
    }),
    messaging: createEventSubscriptionObservableItem({
      active: false,
      field: "onPostChatEventContent",
      modules: [
        "launchpad",
        "dates",
        "tickets",
        "gallery",
        "members",
        "store",
        "messaging",
        "entry",
        "locations",
        "sharing",
      ],
      action: {
        type: "POST_MESSAGE",
        property: "message",
        recalculate: true,
      },
      params: { RoomName: "main" },
    }),
    entry: createEventSubscriptionObservableItem({
      active: false,
      field: "onEntryHandshake",
      modules: ["entry"],
      action: {
        type: "ENTRY_HANDSHAKE",
        property: "handshake",
      },
    }),
  };
}

// NOTE: the "list" key must match list of object keys that follow after it
// list is used to iterate through observables to initiate in a specific order
export function createEventSubscriptionConstants() {
  const constants = {};
  constants.observableList = [
    "event",
    "sharing",
    "locations",
    "messaging",
    "entry",
  ];

  return constants;
}
